import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { Plus } from 'src/assets/icons/customIcons/Plus'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

export default function AdministratorPortfolioCompetenciesPage() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('portfolio:competencies')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const locale = useLocale()
  const t = useTranslatable()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  const [searchParams] = useSearchParams({
    page: '1',
    perPage: '10',
  })
  const page = searchParams.get('page')
  const perPage = searchParams.get('perPage')

  const { data: competencies } = useApi({
    endpoint: Api.getAdministratorPortfolioCompetencies,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          page: Number(page!),
          perPage: Number(perPage!),
        },
      }),
      [headers, page, perPage]
    ),
  })

  const breadcrumbsItems = [
    {
      page: `${t('portfolio:portfolio')} - ${t('portfolio:competencies')}`,
      path: `/${locale}/administrator/portfolio/competencies`,
    },
  ]

  return (
    <Section
      title={t('portfolio:competencies')}
      breadcrubms={breadcrumbsItems}
      icon={<PortfolioIcon />}
      rightElement={
        canManagePortfolio ? (
          <Link to={`/${locale}/administrator/portfolio/competencies/add`}>
            <Button variant="blue" className="flex items-center">
              <Plus color="white" /> <span className="ml-2">{t('common:add')}</span>
            </Button>
          </Link>
        ) : undefined
      }
    >
      <PaginationTop pagination={competencies.meta.pagination} />
      <Table.Table className="mt-7 w-full">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('common:unique')}</Table.Th>
            <Table.Th>{t('common:name')}</Table.Th>
            <Table.Th>{t('common:created_at')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {competencies.data.map((competency) => (
            <Table.Tr key={competency.id}>
              <Table.Td>{competency.id}</Table.Td>
              <Table.Td>
                <Link
                  to={`/${locale}/administrator/portfolio/competencies/${competency.id}`}
                  className="mr-2 text-blue-500 hover:underline"
                >
                  {competency.name}
                </Link>
              </Table.Td>
              <Table.Td className="whitespace-nowrap">{competency.createdAt}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table.Table>
      <PaginationBottom pagination={competencies.meta.pagination} />
    </Section>
  )
}
